import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import apis from "../../../../api/api.actions";

const PASSWORD_RULES_MESSAGE =
  "Password must include uppercase, lowercase, number, and special character.";

function AdminResetPasswordModal({ show, onHide, token }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    setCurrentPassword("");
    setNewPassword("");
    setNewPasswordConfirmation("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowNewPasswordConfirmation(false);
    setSubmitting(false);
  }, [show]);

  const isPasswordValid = (password) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialCharacter = /[^A-Za-z0-9]/.test(password);
    return hasUppercase && hasLowercase && hasNumber && hasSpecialCharacter;
  };

  const showValidationErrors = (errors) => {
    const html = errors
      .map((e) => `<div style="text-align:left;margin-top:4px;">- ${e}</div>`)
      .join("");
    Swal.fire({
      icon: "error",
      title: "Validation Error",
      html: `<div style="font-size:14px;">${html}</div>`,
      confirmButtonText: "OK",
      confirmButtonColor: "#1c2541",
    });
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const errors = [];

    if (!currentPassword) {
      errors.push("Current password is required.");
    }
    if (!newPassword) {
      errors.push("New password is required.");
    }
    if (!newPasswordConfirmation) {
      errors.push("Confirm new password is required.");
    }
    if (newPassword && newPasswordConfirmation && newPassword !== newPasswordConfirmation) {
      errors.push("New password and confirmation do not match.");
    }
    if (newPassword && !isPasswordValid(newPassword)) {
      errors.push(PASSWORD_RULES_MESSAGE);
    }

    if (errors.length > 0) {
      showValidationErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      const res = await apis.createData(
        "v1/admin/change-password",
        {
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirmation: newPasswordConfirmation,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );

      const successMessage =
        res?.message || res?.data?.message || "Password updated successfully.";

      onHide?.();

      Swal.fire({
        icon: "success",
        title: "Password Reset Successful",
        text: successMessage,
        confirmButtonText: "OK",
        confirmButtonColor: "#1c2541",
      });
    } catch (err) {
      console.error("Update password failed:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Update password failed. Please try again.";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: String(msg),
        confirmButtonText: "OK",
        confirmButtonColor: "#1c2541",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      centered
      dialogClassName="reset-pass-modal"
      contentClassName="modal-content-reset"
      backdrop="static"
    >
      <div style={{ padding: "20px" }}>
        <button
          type="button"
          className="reset-pass-close"
          onClick={onHide}
          aria-label="Close"
          disabled={submitting}
        >
          &times;
        </button>

        <div className="reset-pass-header">Reset Password</div>

        <div className="reset-pass-body d-flex justify-content-center align-items-center flex-column">
          <div>
            <img src="/resources/img/login/login-avatar.png" alt="" />
          </div>
          <div className="reset-pass-label">
            Enter your current password and new password
          </div>

          <div className="w-100 reset-pass-fields">
            <div className="reset-pass-field mb-2">
              <label className="reset-pass-field-label">
                Current Password <span className="reset-pass-required">*</span>
              </label>
              <div className="reset-pass-input-wrap">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  className="form-control reset-pass-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="reset-pass-eye"
                  onClick={() => setShowCurrentPassword((prev) => !prev)}
                  aria-label={
                    showCurrentPassword
                      ? "Hide current password"
                      : "Show current password"
                  }
                >
                  <i
                    className={`fa ${
                      showCurrentPassword ? "fa-eye-slash" : "fa-eye"
                    }`}
                  ></i>
                </button>
              </div>
            </div>

            <div className="reset-pass-field mb-2">
              <label className="reset-pass-field-label">
                New Password <span className="reset-pass-required">*</span>
              </label>
              <div className="reset-pass-input-wrap">
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="form-control reset-pass-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="reset-pass-eye"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  aria-label={
                    showNewPassword
                      ? "Hide new password"
                      : "Show new password"
                  }
                >
                  <i
                    className={`fa ${
                      showNewPassword ? "fa-eye-slash" : "fa-eye"
                    }`}
                  ></i>
                </button>
              </div>
              <div className="reset-pass-hint">{PASSWORD_RULES_MESSAGE}</div>
            </div>

            <div className="reset-pass-field mb-1">
              <label className="reset-pass-field-label">
                Confirm New Password{" "}
                <span className="reset-pass-required">*</span>
              </label>
              <div className="reset-pass-input-wrap">
                <input
                  type={showNewPasswordConfirmation ? "text" : "password"}
                  className="form-control reset-pass-input"
                  value={newPasswordConfirmation}
                  onChange={(e) =>
                    setNewPasswordConfirmation(e.target.value)
                  }
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="reset-pass-eye"
                  onClick={() =>
                    setShowNewPasswordConfirmation((prev) => !prev)
                  }
                  aria-label={
                    showNewPasswordConfirmation
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                >
                  <i
                    className={`fa ${
                      showNewPasswordConfirmation
                        ? "fa-eye-slash"
                        : "fa-eye"
                    }`}
                  ></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="reset-pass-action">
          <button
            className="reset-pass-button"
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default AdminResetPasswordModal;
